import type { GuidebookCollapseMode } from "../../types/guidebook";
import { ReactNode, useId, useMemo, useState } from "react";
import {
  getGuidebookText,
  createGuidebookTextModel,
  AUTO_COLLAPSE_LIMIT,
  getGuidebookTextPreview,
  shouldCollapseGuidebookText,
} from "../../utils/guidebookText";
import * as S from "./styles";

interface Props {
  html: string;
  mode?: GuidebookCollapseMode | null;
  title: string;
  children: ReactNode;
  renderProgressiveContent?: (html: string) => ReactNode;
  fontColor?: string;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
}

export default function GuidebookTextBlock({
  html,
  mode,
  title,
  children,
  renderProgressiveContent,
  ...typography
}: Props) {
  const model = useMemo(() => createGuidebookTextModel(html), [html]);
  const text = model.text;
  const collapsible = shouldCollapseGuidebookText(text, mode);
  const [view, setView] = useState({
    html,
    mode,
    expanded: false,
    visibleLimit: AUTO_COLLAPSE_LIMIT,
  });
  // Reset before committing changed content, including resolved shortcode changes.
  if (view.html !== html || view.mode !== mode) {
    setView({ html, mode, expanded: false, visibleLimit: AUTO_COLLAPSE_LIMIT });
  }
  const progressive = mode === "progressive";
  const fullyExpanded = progressive
    ? view.visibleLimit >= model.characterCount
    : view.expanded;
  const progressiveHtml = useMemo(
    () =>
      progressive && !fullyExpanded
        ? model.getProgressiveHtml(view.visibleLimit)
        : "",
    [progressive, fullyExpanded, model, view.visibleLimit],
  );
  const contentId = useId();

  return (
    <S.Block {...typography}>
      <div id={contentId}>
        {collapsible && !fullyExpanded ? (
          progressive ? (
            renderProgressiveContent ? (
              renderProgressiveContent(progressiveHtml)
            ) : (
              <S.Preview>{getGuidebookText(progressiveHtml)}…</S.Preview>
            )
          ) : (
            <S.Preview>{getGuidebookTextPreview(text)}</S.Preview>
          )
        ) : (
          children
        )}
      </div>
      {collapsible && (
        <S.Toggle
          type="button"
          aria-expanded={fullyExpanded}
          aria-controls={contentId}
          aria-label={`${fullyExpanded ? "Read less" : "Read more"}: ${title}`}
          onClick={() =>
            setView((current) =>
              progressive
                ? {
                    ...current,
                    visibleLimit: fullyExpanded
                      ? AUTO_COLLAPSE_LIMIT
                      : current.visibleLimit + AUTO_COLLAPSE_LIMIT,
                  }
                : { ...current, expanded: !current.expanded },
            )
          }
        >
          {fullyExpanded ? "Read less" : "Read more"}
        </S.Toggle>
      )}
    </S.Block>
  );
}
